import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import type { ChangeLogEntry, EditPayload, ExecutePayload } from '../changelog'
import { formatActor, getRelativeTime, getTargetUrl, humanizeField } from '../utils'
import { ChangeLogItem } from './ChangeLogItem'
import { WemoloTextLink } from '../../../components/Wemolo'
import styles from './ChangeLogItem.module.css'

const renderTargetLink = (
    entry: ChangeLogEntry,
    deviceNameMap: Record<string, string>,
    onNavigate: (to: string) => void
): React.ReactNode => {
    const displayName =
        entry.target_type === 'device' ? (deviceNameMap[entry.target_id] ?? entry.target_id) : entry.target_id
    const targetUrl = getTargetUrl(entry.target_type, entry.target_id)
    return (
        <WemoloTextLink
            size="small"
            className={styles.targetLink}
            href={targetUrl}
            onClick={(event) => {
                event.preventDefault()
                onNavigate(targetUrl)
            }}
        >
            {displayName}
        </WemoloTextLink>
    )
}

const renderSummary = (
    entry: ChangeLogEntry,
    deviceNameMap: Record<string, string>,
    onNavigate: (to: string) => void
): React.ReactNode => {
    if (entry.operation === 'execute') {
        const p = entry.payload as ExecutePayload
        const action = p.action_type === 'force_update' ? 'triggered force update' : 'sent SMS'
        return (
            <>
                <b>{action}</b>
                {' for '}
                {renderTargetLink(entry, deviceNameMap, onNavigate)}
            </>
        )
    }

    const p = entry.payload as EditPayload
    if (p.changes.length === 1) {
        const { field, before, after } = p.changes[0]
        return (
            <>
                {'updated '}
                <b>{humanizeField(field)}</b>
                {' from '}
                <b>{before || '—'}</b>
                {' to '}
                <b>{after || '—'}</b>
                {' on '}
                {renderTargetLink(entry, deviceNameMap, onNavigate)}
            </>
        )
    }

    return (
        <>
            {'updated '}
            <b>{p.changes.length} settings</b>
            {' on '}
            {renderTargetLink(entry, deviceNameMap, onNavigate)}
        </>
    )
}

interface ChangeLogItemContainerProps {
    entry: ChangeLogEntry
    isLast: boolean
    deviceNameMap: Record<string, string>
    deviceTypeMap: Record<string, string>
}

export const ChangeLogItemContainer = ({
    entry,
    isLast,
    deviceNameMap,
    deviceTypeMap
}: ChangeLogItemContainerProps) => {
    const history = useHistory()
    const [actorTooltipVisible, setActorTooltipVisible] = useState(false)
    const [timestampTooltipVisible, setTimestampTooltipVisible] = useState(false)
    const [expanded, setExpanded] = useState(false)

    const actor = formatActor(entry.actor)
    const deviceType = entry.target_type === 'device' ? deviceTypeMap[entry.target_id] : undefined
    const summaryNode = renderSummary(entry, deviceNameMap, (to) => history.push(to))
    const relativeTime = getRelativeTime(entry.timestamp)
    const absoluteTime = new Date(entry.timestamp).toLocaleString()
    const actionType = entry.operation === 'execute' ? (entry.payload as ExecutePayload).action_type : undefined
    const VISIBLE_LIMIT = 5
    const rawEditPayload = entry.operation === 'edit' ? (entry.payload as EditPayload) : undefined
    const editPayload = rawEditPayload
        ? {
              ...rawEditPayload,
              changes: expanded ? rawEditPayload.changes : rawEditPayload.changes.slice(0, VISIBLE_LIMIT)
          }
        : undefined
    const hiddenCount = rawEditPayload && !expanded ? Math.max(0, rawEditPayload.changes.length - VISIBLE_LIMIT) : 0
    const executePayload = entry.operation === 'execute' ? (entry.payload as ExecutePayload) : undefined

    return (
        <ChangeLogItem
            isLast={isLast}
            targetType={entry.target_type}
            operation={entry.operation}
            deviceType={deviceType}
            actionType={actionType}
            actor={actor}
            actorTooltipVisible={actorTooltipVisible}
            onActorMouseEnter={() => actor.tooltip && setActorTooltipVisible(true)}
            onActorMouseLeave={() => setActorTooltipVisible(false)}
            summaryNode={summaryNode}
            relativeTime={relativeTime}
            absoluteTime={absoluteTime}
            timestampTooltipVisible={timestampTooltipVisible}
            onTimestampMouseEnter={() => setTimestampTooltipVisible(true)}
            onTimestampMouseLeave={() => setTimestampTooltipVisible(false)}
            editPayload={editPayload}
            executePayload={executePayload}
            hiddenCount={hiddenCount}
            onToggleExpand={
                rawEditPayload && rawEditPayload.changes.length > VISIBLE_LIMIT
                    ? () => setExpanded((e) => !e)
                    : undefined
            }
        />
    )
}
